/**
 * Metadata de presentación para la cover generativa de un libro.
 *
 * Fuente de verdad: el `/catalog` (Workstream A agrega `accent`/`motif`/
 * `category`/`blurb`/`featured` a `listen_books`). Mientras un libro no las
 * tenga, las derivamos de forma DETERMINÍSTICA desde el slug (hash estable) para
 * que la cover nunca quede vacía. Si el backend las manda, mandan ellas.
 */

import type { CatalogBook } from './types.js';

export type CoverMotif = 'orbit' | 'waves' | 'bloom' | 'strata' | 'split' | 'loop';

export interface CoverMeta {
  accent: string;
  motif: CoverMotif;
  category: string;
  blurb: string;
  featured: boolean;
}

/** Paleta de accents para las covers (tonos cálidos/saturados, buen contraste
 *  con texto oscuro `#0c0d15`). */
const ACCENTS = [
  '#d4f23e', // lime
  '#ff9d6c', // coral
  '#a78bfa', // purple
  '#7cc4ff', // cyan
  '#f7c948', // amber
  '#6ee7b7', // mint
] as const;

const MOTIFS: readonly CoverMotif[] = ['orbit', 'waves', 'bloom', 'strata', 'split', 'loop'];

/**
 * Slugs que tienen una cover ILUSTRADA real (webp en `public/covers/<slug>.webp`,
 * generada con GPT Image 2 bajo la dirección de arte de marca: near-black + acento
 * del libro + orbe-firma púrpura/lima). Cuando el slug está acá, `BookCover` pinta
 * la imagen y conmuta el texto a claro; si no, cae al cover generativo (gradiente +
 * motif). Agregar una cover nueva = sumar el webp + el slug acá.
 */
const BOOKS_WITH_COVER: ReadonlySet<string> = new Set([
  'atomic-habits',
  'indio-solari',
  'maradona',
  'netflix',
  'the-last-message',
  'what-if',
  'impossible-interviews',
  'gringo-mode',
  'how-english-got-weird',
  'trust-no-one',
  'the-perfect-heist',
  'the-night-shift',
  'maintenance-hours',
]);

/** URL de la cover ilustrada del libro, o `null` si no tiene (→ cover generativo). */
export function coverImageUrl(slug: string): string | null {
  return BOOKS_WITH_COVER.has(slug) ? `/covers/${slug}.webp` : null;
}

/** Hash determinístico simple (djb2) de un string → entero no negativo. */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Resuelve la metadata de cover: backend > derivada del slug. */
export function deriveCoverMeta(book: CatalogBook): CoverMeta {
  const h = hash(book.slug);
  const accentFallback = ACCENTS[h % ACCENTS.length] ?? ACCENTS[0];
  const motifFallback = MOTIFS[(h >> 3) % MOTIFS.length] ?? 'orbit';
  return {
    accent: book.accent ?? accentFallback,
    motif: (book.motif as CoverMotif | undefined) ?? motifFallback,
    category: book.category ?? 'Resumen',
    blurb: book.blurb ?? book.description ?? '',
    featured: book.featured ?? false,
  };
}
